import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RcFile } from "antd/es/upload";
import LocalStorageService from "../../utils/LocalStorageService";
import FetchService from "../FetchService";

type DataPortfolioType = {
  title: string;
  description: string;
  url_website: string;
  image: string;
};
type UpdatePortfolioReuest = {
  title: string;
  description: string;
  url_website: string;
  image: string;
  id_port: string;
};

type ImagePortfolioType = {
  _id: string;
  name: string;
  url_image: string;
  createdAt: string;
};

type DataResponseType = {
  status: number;
  data?: ImagePortfolioType[];
};

// type UploadImageResponse = {
//   status: number;
//   data?: {
//     name: string;
//     url_image: string;
//   };
// };
type UploadImageResponse = {
  status: number;
  data?: {
    name: string;
    url_image: string;
    _id: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
};

type PortfolioResponse = {
  status: number;
  data?: {
    _id: string;
    title: string;
    description: string;
    url_website: string;
    image: string;
    createdAt: Date;
    updatedAt: Date;
    __v: number;
  }[];
};

type ImageDesignResponse = {
  status: number;
  data?: {
    _id: string;
    name: string;
    url_image: string;
    createdAt: Date;
    updatedAt: Date;
    __v: number;
  }[];
};

type UploadImageDesignResponse = {
  status: number;
  message: string;
  data?: {
    name: string;
    url_image: string;
    _id: string;
    createdAt: Date;
    updatedAt: Date;
    __v: number;
  }[];
};
type LoginSuccessResponse = {
  status: number;
  message: string;
  token: string;
};

type AuthenResponse = {
  status: number;
  data?: {
    _id: string;
    user_name: string;
    email: string;
  };
  message?: string;
};

const PortfolioService = createApi({
  reducerPath: "register_service",
  baseQuery: FetchService,
  tagTypes: [],
  endpoints: (builder) => ({
    postCreatePortfolio: builder.mutation<
      { status: number; message: string },
      DataPortfolioType
    >({
      query: (data) => {
        return {
          url: "/api/portfolio",
          method: "POST",
          body: data,
        };
      },
    }),
    putUpdatePortfolio: builder.mutation<
      { status: number; message: string },
      UpdatePortfolioReuest
    >({
      query: (data) => {
        return {
          url: `/api/portfolio/${data.id_port}`,
          method: "PUT",
          body: data,
        };
      },
    }),
    deletePortfolio: builder.mutation<
      { status: number; message: string },
      { id: string }
    >({
      query: (data) => {
        return {
          url: `/api/portfolio/${data.id}`,
          method: "DELETE",
        };
      },
    }),

    getPortfolio: builder.mutation<PortfolioResponse, void>({
      query: (data) => {
        return {
          url: "/api/portfolio",
          method: "GET",
          // headers: { "content-type": "application/json" },
        };
      },
      transformResponse: (response: PortfolioResponse) => {
        const sortedData =
          response.data?.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          ) || [];
        return { ...response, data: sortedData };
      },
    }),
    getImagePortfolio: builder.mutation<DataResponseType, void>({
      query: () => {
        return {
          url: "/api/image",
          method: "GET",
        };
      },
      transformResponse: (response: DataResponseType) => {
        const sortedData =
          response.data?.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          ) || [];
        return { ...response, data: sortedData };
      },
    }),
    postUploadImagePortfolio: builder.mutation<UploadImageResponse, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append("image", file);
        return {
          url: "/api/image/upload",
          method: "POST",
          body: formData,
          // headers: {
          //   "Content-Type": "multipart/form-data",
          // },
        };
      },
    }),

    deleteImagePortfolio: builder.mutation<
      { status: number; message: string },
      { id_image: string }
    >({
      query: (data) => {
        return {
          url: `/api/image/delete/${data.id_image}`,
          method: "DELETE",
        };
      },
    }),
    getImageDesign: builder.mutation<ImageDesignResponse, void>({
      query: (data) => {
        return {
          url: "/api/image/image-design",
          method: "GET",
          headers: {
            "content-type": "application/json",
          },
        };
      },
      transformResponse: (response: ImageDesignResponse) => {
        const sortedData =
          response.data?.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          ) || [];
        return { ...response, data: sortedData };
      },
    }),
    deleteImageDesign: builder.mutation<
      { status: number; message: string },
      { id_image: string }
    >({
      query: (data) => {
        return {
          url: `/api/image/image-design/${data.id_image}`,
          method: "DELETE",
        };
      },
    }),
    uploadImageDesign: builder.mutation<UploadImageDesignResponse, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append("image", file);

        return {
          url: `/api/image/upload-image-design`,
          method: "POST",
          body: formData,
        };
      },
    }),
    loginService: builder.mutation<
      LoginSuccessResponse,
      { user_name: string; password: string }
    >({
      query: (data) => {
        return {
          url: `/api/login`,
          method: "POST",
          body: data,
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data.token) {
            LocalStorageService.setAccessToken(data.token);
          }
        } catch (error) {
          return;
        }
      },
    }),
    authenAdmin: builder.mutation<AuthenResponse, void>({
      query: (data) => {
        return {
          url: `/api/authentication`,
          method: "GET",
        };
      },
    }),
  }),
});

export const {
  usePostCreatePortfolioMutation,
  usePutUpdatePortfolioMutation,
  useGetPortfolioMutation,
  useGetImagePortfolioMutation,
  usePostUploadImagePortfolioMutation,
  useDeleteImagePortfolioMutation,
  useDeletePortfolioMutation,
  useGetImageDesignMutation,
  useDeleteImageDesignMutation,
  useUploadImageDesignMutation,
  useLoginServiceMutation,
  useAuthenAdminMutation,
} = PortfolioService;

export default PortfolioService;
